import React, {useEffect, useRef, useState} from 'react'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {Modal, Button} from 'react-bootstrap'
import {FaTimesCircle, FaCheckCircle} from 'react-icons/fa'
import Astericsign from './Astericsign'
import {
  addressValidation,
  emailValidation,
  firstNameValidation,
  lastNameValidation,
  phoneNumberValidation,
} from './helper/ValidationHelperComponent'
import PrimaryButton from '../common/buttons/PrimaryButton'

interface FormValues {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  address: string
  clinic_name: string
  clinic_alias: string
  auto_convert_loaned_to_rental: boolean
}

interface DoctorFormProps {
  isOpen: boolean
  onClose: () => void
  handleSubmit: (values: any, resetForm: () => void) => void
  initialData?: any
  title?: string
}

const validationSchemas = Yup.object({
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  email: emailValidation,
  phone_number: phoneNumberValidation,
  address: addressValidation,
  clinic_name: Yup.string().max(100, 'Clinic name must be at most 100 characters').nullable(),
  clinic_alias: Yup.string()
    .max(10, 'Clinic alias must be at most 10 characters')
    .matches(/^[A-Za-z0-9]*$/, 'Clinic alias can only contain letters and numbers')
    .nullable(),
  auto_convert_loaned_to_rental: Yup.boolean(),
})

// Helper function to normalize phone number to E.164 format
const normalizePhoneToE164 = (phone: string | null | undefined): string => {
  if (!phone) return ''
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '')
  // If it already starts with +, return as is
  if (cleaned.startsWith('+')) return cleaned
  // If it's all digits and not empty, add + prefix
  if (cleaned.length > 0) return `+${cleaned}`
  return ''
}

function DoctorForm({isOpen, onClose, handleSubmit, initialData, title}: DoctorFormProps) {
  const resetFormRef = useRef<(() => void) | null>(null)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [formDirty, setFormDirty] = useState(false)

  const initialValues: FormValues = {
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    email: initialData?.email || '',
    phone_number: normalizePhoneToE164(initialData?.phone_number),
    address: initialData?.address || '',
    clinic_name: initialData?.clinic_name || '',
    clinic_alias: initialData?.clinic_alias || '',
    auto_convert_loaned_to_rental: initialData?.auto_convert_loaned_to_rental !== false,
  }

  const handleClose = () => {
    if (formDirty) {
      setShowDiscardModal(true)
    } else {
      onClose()
    }
  }

  const discardChanges = () => {
    resetFormRef.current?.()
    setFormDirty(false)
    setShowDiscardModal(false)
    onClose()
  }

  // reset form when user close and re-open the form
  useEffect(() => {
    if (isOpen && resetFormRef.current) {
      resetFormRef.current()
      setFormDirty(false)
    }
  }, [isOpen])

  return (
    <>
      {isOpen && (
        <div className='modal-backdrop show' style={{backgroundColor: 'rgba(0,0,0,0.5)'}} />
      )}

      <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} tabIndex={-1}>
        <div className='modal-dialog modal-dialog-centered modal-lg'>
          <div className='modal-content rounded-4 shadow'>
            <div className='modal-header bg-light border-bottom-0'>
              <h5 className='modal-title fw-semibold'>{title ?? 'Patient Form'}</h5>
              <button type='button' className='btn-close' onClick={handleClose}></button>
            </div>

            <div className='modal-body p-4'>
              <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchemas}
                onSubmit={({...values}, {setSubmitting, resetForm}) => {
                  const normalizedValues = {
                    ...values,
                    email: values.email?.toLowerCase().trim() || '',
                  }        
                  handleSubmit(normalizedValues, resetForm)
                  resetForm({
                    values: {
                      first_name: '',
                      last_name: '',
                      phone_number: '',
                      email: '',
                      address: '',
                      clinic_name: '',
                      clinic_alias: '',
                      auto_convert_loaned_to_rental: true,
                    },
                  })
                  setFormDirty(false)
                  onClose()
                  setSubmitting(false)
                }}
              >
                {({isSubmitting, values, setFieldValue, resetForm}) => {
                  resetFormRef.current = resetForm

                  return (
                    <Form onChange={() => setFormDirty(true)}>
                      <>
                        <div className='mb-3'>
                          <div className='d-flex gap-3 mb-3'>
                            <div className='flex-grow-1'>
                              <label className='form-label'>
                                First Name <Astericsign />
                              </label>
                              <Field
                                name='first_name'
                                className='form-control'
                                onInput={(e: any) => {
                                  e.currentTarget.value = e.currentTarget.value.replace(
                                    /[^a-zA-Z\s]/g,
                                    ''
                                  )
                                }}
                              />
                              <ErrorMessage
                                name='first_name'
                                component='div'
                                className='text-danger'
                              />
                            </div>

                            <div className='flex-grow-1'>
                              <label className='form-label'>
                                Last Name <Astericsign />{' '}
                              </label>
                              <Field
                                name='last_name'
                                className='form-control'
                                onInput={(e: any) => {
                                  e.currentTarget.value = e.currentTarget.value.replace(
                                    /[^a-zA-Z\s]/g,
                                    ''
                                  )
                                }}
                              />
                              <ErrorMessage
                                name='last_name'
                                component='div'
                                className='text-danger'
                              />
                            </div>
                          </div>
                          <div className='mb-3'>
                            <div className='d-flex gap-3 mb-3'>
                              <div className='flex-grow-1'>
                                <label className='form-label'>
                                  Email <Astericsign />
                                </label>
                                <Field name='email' type='email' className='form-control' />
                                <ErrorMessage
                                  name='email'
                                  component='div'
                                  className='text-danger'
                                />
                              </div>
                              <div className='flex-grow-1'>
                                <label className='form-label'>
                                  Phone <Astericsign />
                                </label>
                                <PhoneInput
                                  defaultCountry='US'
                                  international
                                  value={values.phone_number}
                                  onChange={(value) => setFieldValue('phone_number', value)}
                                  maxLength={15}
                                  inputMode='numeric'
                                  title='Phone Number must be exactly 15 digits'
                                  onInput={(e: any) => {
                                    e.currentTarget.value = e.currentTarget.value.replace(
                                      /[^0-9+]/g,
                                      ''
                                    )
                                  }}
                                  className='form-control'
                                />
                                <ErrorMessage
                                  name='phone_number'
                                  component='div'
                                  className='text-danger'
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label'>Address</label>
                          <Field name='address' as='textarea' className='form-control' />
                          <ErrorMessage name='address' component='div' className='text-danger' />
                        </div>

                        {/* Clinic Settings */}
                        <div className='mb-3'>
                          <div className='d-flex gap-3 mb-3'>
                            <div className='flex-grow-1'>
                              <label className='form-label'>Clinic Name</label>
                              <Field name='clinic_name' className='form-control' placeholder='Enter clinic name' />
                              <ErrorMessage name='clinic_name' component='div' className='text-danger' />
                            </div>
                            <div className='flex-grow-1'>
                              <label className='form-label'>Clinic Alias</label>
                              <Field
                                name='clinic_alias'
                                className='form-control'
                                placeholder='e.g., H360'
                                maxLength={10}
                                onInput={(e: any) => {
                                  e.currentTarget.value = e.currentTarget.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
                                }}
                              />
                              <ErrorMessage name='clinic_alias' component='div' className='text-danger' />
                            </div>
                          </div>
                        </div>
                        <div className='mb-3'>
                          <div className='form-check form-switch'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              role='switch'
                              id='autoConvertSwitch'
                              checked={values.auto_convert_loaned_to_rental}
                              onChange={(e) => {
                                setFieldValue('auto_convert_loaned_to_rental', e.target.checked)
                                setFormDirty(true)
                              }}
                            />
                            <label className='form-check-label' htmlFor='autoConvertSwitch'>
                              Auto-Convert Loaned to Rental
                            </label>
                          </div>
                          <small className='text-muted'>
                            When enabled, loaned devices are automatically converted to rental when assigned to a patient
                          </small>
                        </div>
                      </>
                      <div className='d-flex justify-content-end'>
                        <PrimaryButton type='submit' className=''>
                          Submit
                        </PrimaryButton>
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Discard Modal */}
      <Modal show={showDiscardModal} onHide={() => setShowDiscardModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTimesCircle className='text-danger me-2' />
            Unsaved Changes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have entered information that hasn’t been saved yet. Are you sure you want to discard
          your changes?{' '}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='light'
            className='border shadow-sm text-muted px-4'
            onClick={() => setShowDiscardModal(false)}
          >
            Keep Editing
          </Button>
          <Button variant='danger' onClick={discardChanges}>
            <FaCheckCircle className='me-2' />
            Discard Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default DoctorForm
